import { successNotification, warningNotification } from "@app/common/components/notifications";
import { formattedDataSelect } from "@app/common/utils/utils";
import {
  useCreatePatient,
  useGetCities,
  useGetDocumentTypes,
  useGetProvinces
} from "@app/services/common/hooks";
import { useAuthentication } from "@app/store/useAuthtentication";
import { Box, Button, Grid, Modal, Select, TextInput } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { ICreatePatientForm, initialForm } from "../../types/types";

const NIT_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

function calculateVerificationDigit(nit: string): string {
  const digits = nit.replace(/\D/g, "");
  if (!digits.length) {
    return "";
  }
  const reversed = digits.split("").reverse();
  let sum = 0;
  for (let i = 0; i < reversed.length && i < NIT_WEIGHTS.length; i++) {
    sum += Number(reversed[i]) * NIT_WEIGHTS[i];
  }
  const remainder = sum % 11;
  if (remainder >= 2) {
    return String(11 - remainder);
  }
  return String(remainder);
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialDocumentTypeId?: string;
  initialDocumentNumber?: string;
}

const CreatePatientModal = ({
  isOpen,
  onClose,
  onCreated,
  initialDocumentTypeId,
  initialDocumentNumber
}: Props) => {
  const { idAccount } = useAuthentication((store) => store.auth.dataBiowel);
  const [form, setForm] = useState<ICreatePatientForm>(initialForm);

  useEffect(() => {
    if (isOpen) {
      setForm((prev) => ({
        ...prev,
        documentTypeId: initialDocumentTypeId ?? prev.documentTypeId,
        documentNumber: initialDocumentNumber ?? prev.documentNumber
      }));
    }
  }, [isOpen, initialDocumentTypeId, initialDocumentNumber]);

  const { data: documentTypes = { results: [], rowTotal: 0 } } = useGetDocumentTypes();

  const isNit = useMemo(
    () =>
      documentTypes.results.find((dt) => dt.id === Number(form.documentTypeId))?.description ===
      "NIT",
    [documentTypes.results, form.documentTypeId]
  );

  const verificationDigit = useMemo(
    () => (isNit ? calculateVerificationDigit(form.documentNumber) : ""),
    [isNit, form.documentNumber]
  );
  const { data: provinces } = useGetProvinces(idAccount);
  const { data: cities } = useGetCities(form.provinceId ? Number(form.provinceId) : undefined);

  const createPatient = useCreatePatient(
    () => {
      successNotification("Tercero creado correctamente", "El tercero ha sido creado exitosamente");
      setForm(initialForm);
      onCreated();
      onClose();
    },
    () => {
      warningNotification("Error al crear el tercero", "Intenta de nuevo");
    }
  );

  const handleChange = (field: keyof ICreatePatientForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.documentTypeId) {
      warningNotification("El tipo de documento es obligatorio");
      return;
    }
    if (!form.documentNumber) {
      warningNotification("El número de identificación es obligatorio");
      return;
    }
    if (isNit && !form.businessName) {
      warningNotification("La razón social es obligatoria");
      return;
    }
    if (!isNit && !form.firstName) {
      warningNotification("El nombre es obligatorio");
      return;
    }
    if (!isNit && !form.lastName) {
      warningNotification("El apellido es obligatorio");
      return;
    }
    if (!form.address) {
      warningNotification("La dirección es obligatoria");
      return;
    }
    if (!form.cityId) {
      warningNotification("La ciudad es obligatoria");
      return;
    }
    if (!form.email) {
      warningNotification("El correo electrónico es obligatorio");
      return;
    }

    createPatient.mutate({
      documentTypeId: Number(form.documentTypeId),
      documentNumber: isNit ? `${form.documentNumber}-${verificationDigit}` : form.documentNumber,
      firstName: isNit ? form.businessName : form.firstName,
      lastName: isNit ? "" : form.lastName,
      ...(isNit ? { businessName: form.businessName } : {}),
      address: form.address,
      cityId: Number(form.cityId),
      email: form.email
    });
  };

  const documentTypeOptions = formattedDataSelect(documentTypes.results ?? [], "description", "id");
  const provinceOptions = formattedDataSelect(provinces ?? [], "name", "id");
  const cityOptions = formattedDataSelect(cities ?? [], "name", "id");

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Crear tercero"
      size="lg"
      data-testid="createpatient-drawer"
    >
      <Box className="flex flex-column gap-3" data-testid="createpatient-form">
        <Grid>
          <Grid.Col span={6}>
            <Select
              label="Tipo de documento"
              required
              data={documentTypeOptions}
              searchable
              clearable
              value={form.documentTypeId}
              placeholder="Seleccionar..."
              data-testid="createpatient-doctype"
              onChange={(val) => handleChange("documentTypeId", val ?? "")}
            />
          </Grid.Col>
          <Grid.Col span={isNit ? 4 : 6}>
            <TextInput
              label="No. de identificación"
              required
              placeholder="Escribir..."
              value={form.documentNumber}
              data-testid="createpatient-document"
              onChange={({ target }) => handleChange("documentNumber", target.value)}
            />
          </Grid.Col>
          {isNit && (
            <Grid.Col span={2}>
              <TextInput
                label="Dígito verif."
                readOnly
                value={verificationDigit}
                data-testid="createpatient-verification-digit"
                styles={{
                  input: {
                    textAlign: "center",
                    fontWeight: "bold",
                    backgroundColor: "var(--mantine-color-gray-1)"
                  }
                }}
              />
            </Grid.Col>
          )}
          {isNit && (
            <Grid.Col span={12}>
              <TextInput
                label="Razón social"
                required
                placeholder="Escribir..."
                value={form.businessName}
                data-testid="createpatient-businessname"
                onChange={({ target }) => handleChange("businessName", target.value)}
              />
            </Grid.Col>
          )}
          {!isNit && (
            <Grid.Col span={6}>
              <TextInput
                label="Nombre"
                required
                placeholder="Escribir..."
                value={form.firstName}
                data-testid="createpatient-firstname"
                onChange={({ target }) => handleChange("firstName", target.value)}
              />
            </Grid.Col>
          )}
          {!isNit && (
            <Grid.Col span={6}>
              <TextInput
                label="Apellido"
                required
                placeholder="Escribir..."
                value={form.lastName}
                data-testid="createpatient-lastname"
                onChange={({ target }) => handleChange("lastName", target.value)}
              />
            </Grid.Col>
          )}
          <Grid.Col span={6}>
            <TextInput
              label="Dirección"
              required
              placeholder="Escribir..."
              value={form.address}
              data-testid="createpatient-address"
              onChange={({ target }) => handleChange("address", target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Departamento"
              data={provinceOptions}
              searchable
              clearable
              value={form.provinceId}
              placeholder="Seleccionar..."
              data-testid="createpatient-province"
              onChange={(val) => {
                handleChange("provinceId", val ?? "");
                handleChange("cityId", "");
              }}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Ciudad"
              required
              data={cityOptions}
              searchable
              clearable
              disabled={!form.provinceId}
              value={form.cityId}
              placeholder="Seleccionar..."
              data-testid="createpatient-city"
              onChange={(val) => handleChange("cityId", val ?? "")}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label="Correo electrónico"
              required
              placeholder="Escribir..."
              type="email"
              value={form.email}
              data-testid="createpatient-email"
              onChange={({ target }) => handleChange("email", target.value)}
            />
          </Grid.Col>
        </Grid>
        <Box className="flex justify-end gap-2" mt="md">
          <Button
            variant="outline"
            data-testid="createpatient-cancel"
            onClick={() => {
              setForm(initialForm);
              onClose();
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="gradient"
            loading={createPatient.isPending}
            data-testid="createpatient-submit"
            onClick={handleSubmit}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreatePatientModal;
